import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { inr, todayISO, formatDate } from "@/lib/utils_pharma";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Download, BarChart3, ShoppingBag, Boxes, Printer, AlertCircle, Loader2, Clock, AlertTriangle, Users, Search } from "lucide-react";
import { toast } from "sonner";
import InvoicePreviewDialog from "@/components/InvoicePreviewDialog";
import DeleteBillDialog from "@/components/DeleteBillDialog";
import PatientSalesReportDialog from "@/components/PatientSalesReportDialog";
import ConsolidatedPatientsThermalPrint from "@/components/ConsolidatedPatientsThermalPrint";
import { useAuth } from "@/context/AuthContext";

function firstOfMonth() {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
}

function toISODate(d) {
  return d.toISOString().slice(0, 10);
}

function yesterdayISO() {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return toISODate(d);
}

function startOfWeekISO() {
  const d = new Date();
  const day = d.getDay() || 7;
  d.setDate(d.getDate() - day + 1);
  return toISODate(d);
}

function downloadCSV(name, rows) {
  if (!rows || rows.length === 0) {
    toast.error("Nothing to export");
    return;
  }
  const cols = Object.keys(rows[0]);
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const csv = [cols.join(","), ...rows.map((r) => cols.map((c) => esc(r[c])).join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

function sanitizeFilePart(value) {
  return String(value || "report").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
}

function Stat({ label, value, sub, tone = "default", testid }) {
  const tones = {
    default: "bg-white",
    primary: "bg-primary text-primary-foreground",
    accent: "bg-accent text-accent-foreground",
    destructive: "bg-red-500 text-white",
  };
  return (
    <div className={`min-w-0 rounded-md border border-border p-3 sm:p-3.5 ${tones[tone]}`} data-testid={testid}>
      <div className={`text-[10px] sm:text-[11px] uppercase tracking-wider font-medium truncate ${tone === "default" ? "text-muted-foreground" : "opacity-90"}`}>
        {label}
      </div>
      <div
        className="mt-1.5 text-lg font-semibold tabular-nums tracking-tight truncate"
        style={{ fontFamily: "Chivo" }}
        title={typeof value === "string" || typeof value === "number" ? String(value) : undefined}
      >
        {value}
      </div>
      {sub && (
        <div className={`text-[11px] sm:text-xs mt-0.5 truncate ${tone === "default" ? "text-muted-foreground" : "opacity-90"}`}>
          {sub}
        </div>
      )}
    </div>
  );
}

export default function Reports() {
  const [searchParams, setSearchParams] = useSearchParams();

  const urlTab = searchParams.get("tab");
  const urlRange = searchParams.get("range");

  const [tab, setTab] = useState(() => {
    const validTabs = ["sales", "purchases", "stock", "low-stock", "expiry", "patients"];
    return validTabs.includes(urlTab) ? urlTab : "sales";
  });
  const [datePreset, setDatePreset] = useState(() => (urlTab === "patients" ? "today" : "this_month"));
  const [start, setStart] = useState(() => (urlTab === "patients" ? todayISO() : firstOfMonth()));
  const [end, setEnd] = useState(todayISO());

  // Report Data
  const [sales, setSales] = useState(null);
  const [purchases, setPurchases] = useState(null);
  const [stock, setStock] = useState(null);
  const [expiryReport, setExpiryReport] = useState(null);
  const [lowStockReport, setLowStockReport] = useState(null);
  const [patientReport, setPatientReport] = useState(null);

  // Patient Report sub-state
  const [patientSearch, setPatientSearch] = useState("");
  const [debouncedPatientSearch, setDebouncedPatientSearch] = useState("");
  const [selectedPatientId, setSelectedPatientId] = useState(null);
  const [patientModalOpen, setPatientModalOpen] = useState(false);
  const [showConsolidatedThermal, setShowConsolidatedThermal] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedPatientSearch(patientSearch), 250);
    return () => clearTimeout(t);
  }, [patientSearch]);

  // Filters for sub-reports
  const [expiryFilter, setExpiryFilter] = useState(() => {
    const validRanges = ["expired", "15", "30", "45", "60", "90", "all_expiring", "all"];
    return validRanges.includes(urlRange) ? urlRange : "60";
  });
  const [lowStockFilter, setLowStockFilter] = useState("all");

  useEffect(() => {
    if (urlTab && ["sales", "purchases", "stock", "expiry", "low-stock"].includes(urlTab)) {
      setTab(urlTab);
    }
    if (urlRange && ["expired", "15", "30", "45", "60", "90", "all_expiring", "all"].includes(urlRange)) {
      setExpiryFilter(urlRange);
    }
  }, [urlTab, urlRange]);

  const handleTabChange = (newTab) => {
    setTab(newTab);
    const params = new URLSearchParams(searchParams);
    params.set("tab", newTab);
    if (newTab !== "expiry") {
      params.delete("range");
    }
    setSearchParams(params, { replace: true });
  };

  const handleExpiryFilterChange = (range) => {
    setExpiryFilter(range);
    const params = new URLSearchParams(searchParams);
    params.set("tab", "expiry");
    params.set("range", range);
    setSearchParams(params, { replace: true });
  };

  const [loading, setLoading] = useState({
    sales: false,
    purchases: false,
    stock: false,
    expiry: false,
    lowStock: false,
    patients: false,
  });
  const [errors, setErrors] = useState({
    sales: "",
    purchases: "",
    stock: "",
    expiry: "",
    lowStock: "",
    patients: "",
  });

  const [previewBill, setPreviewBill] = useState(null);
  const [shopInfo, setShopInfo] = useState({});

  const { user } = useAuth();
  const storedUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("pharma_user") || "{}");
    } catch {
      return {};
    }
  }, []);
  const currentUser = user || storedUser;
  const isAdmin = currentUser?.role === "admin";
  const isDeleteEnabled = process.env.REACT_APP_ENABLE_BILL_DELETE !== "false";
  const canDeleteBills = isAdmin && isDeleteEnabled;

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [billToDelete, setBillToDelete] = useState(null);

  const openDeleteModal = (bill) => {
    setBillToDelete(bill);
    setDeleteModalOpen(true);
  };

  const handleBillDeleted = () => {
    setPreviewBill(null);
    loadSales();
  };

  const dateRangeLabel =
    datePreset === "all"
      ? "All Time"
      : datePreset === "custom"
      ? `${formatDate(start)} to ${formatDate(end)}`
      : {
          today: "Today",
          yesterday: "Yesterday",
          this_week: "This Week",
          this_month: "This Month",
        }[datePreset] || (start && end ? `${formatDate(start)} to ${formatDate(end)}` : "All Time");

  useEffect(() => {
    api.get("/settings/shop").then((r) => setShopInfo(r.data)).catch(() => {});
  }, []);

  const openBill = async (billNo) => {
    const bill = sales?.bills?.find((b) => b.bill_no === billNo);
    if (bill) setPreviewBill(bill);
  };

  const loadSales = async () => {
    setLoading((prev) => ({ ...prev, sales: true }));
    setErrors((prev) => ({ ...prev, sales: "" }));
    try {
      const params = {};
      if (datePreset !== "all" && start && end) {
        params.start = start;
        params.end = end;
      }
      const { data } = await api.get("/reports/sales", { params });
      setSales(data);
    } catch (err) {
      setErrors((prev) => ({ ...prev, sales: err?.response?.data?.detail || "Failed to load sales report" }));
      toast.error("Failed to load sales report");
    } finally {
      setLoading((prev) => ({ ...prev, sales: false }));
    }
  };

  const loadPurchases = async () => {
    setLoading((prev) => ({ ...prev, purchases: true }));
    setErrors((prev) => ({ ...prev, purchases: "" }));
    try {
      const params = {};
      if (datePreset !== "all" && start && end) {
        params.start = start;
        params.end = end;
      }
      const { data } = await api.get("/reports/purchases", { params });
      setPurchases(data);
    } catch (err) {
      setErrors((prev) => ({ ...prev, purchases: err?.response?.data?.detail || "Failed to load purchases report" }));
      toast.error("Failed to load purchases report");
    } finally {
      setLoading((prev) => ({ ...prev, purchases: false }));
    }
  };

  const loadStock = async () => {
    setLoading((prev) => ({ ...prev, stock: true }));
    setErrors((prev) => ({ ...prev, stock: "" }));
    try {
      const { data } = await api.get("/reports/stock");
      setStock(data);
    } catch (err) {
      setErrors((prev) => ({ ...prev, stock: err?.response?.data?.detail || "Failed to load stock report" }));
      toast.error("Failed to load stock report");
    } finally {
      setLoading((prev) => ({ ...prev, stock: false }));
    }
  };

  const loadPatientReport = async () => {
    setLoading((prev) => ({ ...prev, patients: true }));
    setErrors((prev) => ({ ...prev, patients: "" }));
    try {
      const params = {};
      if (datePreset !== "all" && start && end) {
        params.start_date = start;
        params.end_date = end;
      }
      if (debouncedPatientSearch.trim()) {
        params.search = debouncedPatientSearch.trim();
      }
      const { data } = await api.get("/reports/patients", { params });
      setPatientReport(data);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Failed to load patient sales report";
      setErrors((prev) => ({ ...prev, patients: msg }));
      toast.error(msg);
    } finally {
      setLoading((prev) => ({ ...prev, patients: false }));
    }
  };

  const loadExpiryStock = async () => {
    setLoading((prev) => ({ ...prev, expiry: true }));
    setErrors((prev) => ({ ...prev, expiry: "" }));
    try {
      const { data } = await api.get("/reports/stock/expiry", {
        params: { filter_type: expiryFilter, active_only: true },
      });
      setExpiryReport(data);
    } catch (err) {
      setErrors((prev) => ({ ...prev, expiry: err?.response?.data?.detail || "Failed to load expiry report" }));
      toast.error("Failed to load expiry stock report");
    } finally {
      setLoading((prev) => ({ ...prev, expiry: false }));
    }
  };

  const loadLowStock = async () => {
    setLoading((prev) => ({ ...prev, lowStock: true }));
    setErrors((prev) => ({ ...prev, lowStock: "" }));
    try {
      const { data } = await api.get("/reports/stock/low-stock", {
        params: { status: lowStockFilter },
      });
      setLowStockReport(data);
    } catch (err) {
      setErrors((prev) => ({ ...prev, lowStock: err?.response?.data?.detail || "Failed to load low stock report" }));
      toast.error("Failed to load low stock report");
    } finally {
      setLoading((prev) => ({ ...prev, lowStock: false }));
    }
  };

  const applyDatePreset = (preset) => {
    setDatePreset(preset);
    if (preset === "all") {
      setStart("");
      setEnd("");
    } else if (preset === "today") {
      setStart(todayISO());
      setEnd(todayISO());
    } else if (preset === "yesterday") {
      setStart(yesterdayISO());
      setEnd(yesterdayISO());
    } else if (preset === "this_week") {
      setStart(startOfWeekISO());
      setEnd(todayISO());
    } else if (preset === "this_month") {
      setStart(firstOfMonth());
      setEnd(todayISO());
    } else if (preset === "custom") {
      if (!start || !end) {
        setStart(firstOfMonth());
        setEnd(todayISO());
      }
    }
  };

  const handleCustomStart = (value) => {
    setDatePreset("custom");
    setStart(value);
  };

  const handleCustomEnd = (value) => {
    setDatePreset("custom");
    setEnd(value);
  };

  useEffect(() => {
    if (tab === "sales") loadSales();
    if (tab === "purchases") loadPurchases();
    if (tab === "stock") loadStock();
    if (tab === "expiry") loadExpiryStock();
    if (tab === "low-stock") loadLowStock();
    if (tab === "patients") loadPatientReport();
    // eslint-disable-next-line
  }, [tab, datePreset, start, end, expiryFilter, lowStockFilter, debouncedPatientSearch]);

  const salesRows = useMemo(
    () =>
      sales?.bills?.map((b) => ({
        id: b.id,
        bill_no: b.bill_no,
        date: formatDate(b.created_at),
        customer: b.customer_name,
        phone: b.customer_phone,
        items: b.items?.length || 0,
        subtotal: b.subtotal,
        discount: b.total_discount,
        cgst: b.cgst,
        sgst: b.sgst,
        grand_total: b.grand_total,
        payment_mode:
          b.payment_mode === "Split" && Array.isArray(b.payments) && b.payments.length > 0
            ? `Split (${b.payments.map((p) => `${p.mode}: ${inr(p.amount)}`).join(", ")})`
            : b.payment_mode,
        raw_payment_mode: b.payment_mode,
        payments: b.payments,
      })) || [],
    [sales]
  );

  const purchaseRows = useMemo(
    () =>
      purchases?.purchases?.map((p) => ({
        invoice_no: p.invoice_no,
        date: formatDate(p.invoice_date || p.created_at),
        supplier: p.supplier_name || "",
        items: p.items?.length || 0,
        subtotal: p.subtotal,
        tax: p.tax_amount,
        total: p.total,
      })) || [],
    [purchases]
  );

  const stockRows = useMemo(
    () =>
      stock?.all?.map((m) => ({
        name: m.name,
        manufacturer: m.manufacturer,
        hsn: m.hsn_code,
        batch: m.batch_no,
        expiry: formatDate(m.expiry_date),
        stock: m.stock,
        mrp: m.mrp,
        purchase_price: m.purchase_price,
        gst_percent: m.gst_percent,
        value_at_cost: (m.stock || 0) * (m.purchase_price || 0),
        value_at_mrp: (m.stock || 0) * (m.mrp || 0),
      })) || [],
    [stock]
  );

  const expiryRows = useMemo(
    () =>
      expiryReport?.items?.map((it) => ({
        medicine: it.medicine_name,
        generic: it.generic_name,
        batch_no: it.batch_no,
        expiry_date: formatDate(it.expiry_date),
        status: it.status,
        days_until_expiry: it.days_until_expiry,
        stock: it.stock,
        unit: it.packing_unit,
        purchase_price: it.purchase_price,
        mrp: it.mrp,
        value_at_cost: it.value_at_cost,
        value_at_mrp: it.value_at_mrp,
      })) || [],
    [expiryReport]
  );

  const lowStockRows = useMemo(
    () =>
      lowStockReport?.items?.map((it) => ({
        medicine: it.name,
        manufacturer: it.manufacturer,
        category: it.category,
        status: it.status,
        stock: it.stock,
        unit: it.packing_unit,
        threshold: it.low_stock_threshold,
        reorder_qty: it.reorder_quantity,
        purchase_price: it.purchase_price,
        mrp: it.mrp,
        value_at_cost: it.value_at_cost,
      })) || [],
    [lowStockReport]
  );

  const csvFileName = (reportName) => {
    const range = reportName.includes("stock")
      ? formatDate(todayISO()).replace(/\//g, "-")
      : datePreset === "all"
      ? "all-time"
      : `${start}_to_${end}`;
    return `pharmacy_${sanitizeFilePart(reportName)}_report_${range}.csv`;
  };

  const printReport = (reportName, rows, columns, summaryItems = []) => {
    if (!rows || rows.length === 0) {
      toast.error("Nothing to print");
      return;
    }
    const title = `${reportName} Report`;
    const period = reportName.includes("Stock") ? `As on ${formatDate(todayISO())}` : dateRangeLabel;
    const printWindow = window.open("", "_blank", "width=1000,height=720");
    if (!printWindow) return toast.error("Popup blocked. Please allow popups to print reports.");
    const header = columns.map((c) => `<th>${c.label}</th>`).join("");
    const body = rows
      .map(
        (row) =>
          `<tr>${columns
            .map(
              (c) =>
                `<td class="${c.align === "right" ? "num" : ""}">${
                  c.format ? c.format(row[c.key], row) : row[c.key] ?? ""
                }</td>`
            )
            .join("")}</tr>`
      )
      .join("");
    const summaryHtml = summaryItems
      .map((s) => `<div class="card"><span>${s.label}</span><strong>${s.value}</strong></div>`)
      .join("");
    printWindow.document.write(`
      <html><head><title>${title}</title><style>
        body{font-family:Arial,sans-serif;color:#111827;padding:24px} h1{margin:0 0 4px}.muted{color:#6b7280;font-size:12px}.summary{display:grid;grid-template-columns:repeat(4,1fr);gap:10px;margin:18px 0}.card{border:1px solid #d1d5db;border-radius:8px;padding:10px}.card span{display:block;color:#6b7280;font-size:10px;text-transform:uppercase;letter-spacing:.08em}.card strong{display:block;margin-top:5px;font-size:16px}table{width:100%;border-collapse:collapse;margin-top:12px;font-size:11px}th,td{border:1px solid #d1d5db;padding:6px;text-align:left}th{background:#f3f4f6}.num{text-align:right}
      </style></head><body><h1>${title}</h1><div class="muted">${shopInfo?.shop_name || "Pharmacy"} · ${period}</div><div class="summary">${summaryHtml}</div><table><thead><tr>${header}</tr></thead><tbody>${body}</tbody></table></body></html>`);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  const salesSummary = [
    { label: "Today's Sales", value: inr(sales?.totals?.gross_sales || 0) },
    { label: "Sales Returns", value: inr(sales?.totals?.sales_returns || 0) },
    { label: "Final Sales", value: inr((sales?.totals?.net_sales ?? sales?.totals?.grand_total) || 0) },
    { label: "GST Collected", value: inr(sales?.totals?.total_tax || 0) },
  ];

  const salesColumns = [
    { key: "bill_no", label: "Bill No." },
    { key: "date", label: "Date" },
    { key: "customer", label: "Customer" },
    { key: "payment_mode", label: "Payment Mode" },
    { key: "items", label: "Items", align: "right" },
    { key: "grand_total", label: "Total Amount", align: "right", format: (v) => inr(v) },
  ];

  const stockSummary = [
    { label: "Stock @ Cost", value: inr(stock?.totals?.stock_value_cost || 0) },
    { label: "Stock @ MRP", value: inr(stock?.totals?.stock_value_mrp || 0) },
    { label: "Low / Out", value: `${stock?.totals?.low_stock_count || 0} / ${stock?.totals?.out_of_stock_count || 0}` },
    { label: "Expiring / Expired", value: `${stock?.totals?.expiring_count || 0} / ${stock?.totals?.expired_count || 0}` },
  ];

  const stockColumns = [
    { key: "name", label: "Medicine" },
    { key: "batch", label: "Batch" },
    { key: "expiry", label: "Expiry" },
    { key: "stock", label: "Stock", align: "right" },
    { key: "purchase_price", label: "Cost", align: "right", format: (v) => inr(v) },
    { key: "mrp", label: "MRP", align: "right", format: (v) => inr(v) },
    { key: "value_at_cost", label: "Value @ Cost", align: "right", format: (v) => inr(v) },
  ];

  const expirySummary = [
    { label: "Expired Batches", value: expiryReport?.summary?.expired_count || 0 },
    { label: "Expiring in 30 Days", value: expiryReport?.summary?.expiring_30_count || 0 },
    { label: "Expiring in 60 Days", value: expiryReport?.summary?.expiring_60_count || 0 },
    { label: "Value @ Cost", value: inr(expiryReport?.summary?.total_value_cost || 0) },
  ];

  const expiryColumns = [
    { key: "medicine", label: "Medicine Name" },
    { key: "batch_no", label: "Batch No." },
    { key: "expiry_date", label: "Expiry Date" },
    { key: "status", label: "Status" },
    { key: "stock", label: "Stock", align: "right" },
    { key: "purchase_price", label: "Cost", align: "right", format: (v) => inr(v) },
    { key: "mrp", label: "MRP", align: "right", format: (v) => inr(v) },
    { key: "value_at_cost", label: "Value @ Cost", align: "right", format: (v) => inr(v) },
  ];

  const lowStockSummary = [
    { label: "Out of Stock", value: lowStockReport?.summary?.out_of_stock_count || 0 },
    { label: "Low Stock Items", value: lowStockReport?.summary?.low_stock_count || 0 },
    { label: "Deficit Units", value: lowStockReport?.summary?.total_deficit_units || 0 },
    { label: "Current Value @ Cost", value: inr(lowStockReport?.summary?.total_value_cost || 0) },
  ];

  const lowStockColumns = [
    { key: "medicine", label: "Medicine Name" },
    { key: "manufacturer", label: "Manufacturer" },
    { key: "status", label: "Status" },
    { key: "stock", label: "Current Stock", align: "right" },
    { key: "threshold", label: "Alert Threshold", align: "right" },
    { key: "reorder_qty", label: "Reorder Deficit", align: "right" },
    { key: "purchase_price", label: "Cost", align: "right", format: (v) => inr(v) },
    { key: "value_at_cost", label: "Value @ Cost", align: "right", format: (v) => inr(v) },
  ];

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Analytics & Audits</div>
          <h1 className="text-4xl font-semibold" style={{ fontFamily: "Chivo" }}>Reports</h1>
        </div>

        {/* Date presets apply to time-bound reports (sales, purchases, patients) */}
        {(tab === "sales" || tab === "purchases" || tab === "patients") && (
          <div className="flex flex-wrap items-center gap-2">
            {[
              { id: "all", label: "All" },
              { id: "today", label: "Today" },
              { id: "yesterday", label: "Yesterday" },
              { id: "this_week", label: "This Week" },
              { id: "this_month", label: "This Month" },
              { id: "custom", label: "Custom" },
            ].map((p) => (
              <Button
                key={p.id}
                variant={datePreset === p.id ? "default" : "outline"}
                size="sm"
                onClick={() => applyDatePreset(p.id)}
                data-testid={`preset-${p.id}`}
              >
                {p.label}
              </Button>
            ))}
            {datePreset !== "all" && (
              <>
                <div>
                  <Label className="text-xs">From</Label>
                  <Input type="date" value={start} onChange={(e) => handleCustomStart(e.target.value)} data-testid="report-start" />
                </div>
                <div>
                  <Label className="text-xs">To</Label>
                  <Input type="date" value={end} onChange={(e) => handleCustomEnd(e.target.value)} data-testid="report-end" />
                </div>
              </>
            )}
          </div>
        )}
      </div>

      <Tabs value={tab} onValueChange={handleTabChange}>
        <TabsList className="bg-secondary flex-wrap h-auto p-1">
          <TabsTrigger value="sales" data-testid="tab-sales">
            <BarChart3 size={14} className="mr-1" /> Sales
          </TabsTrigger>
          <TabsTrigger value="purchases" data-testid="tab-purchases">
            <ShoppingBag size={14} className="mr-1" /> Purchases
          </TabsTrigger>
          <TabsTrigger value="stock" data-testid="tab-stock">
            <Boxes size={14} className="mr-1" /> Full Stock
          </TabsTrigger>
          <TabsTrigger value="low-stock" data-testid="tab-low-stock">
            <AlertTriangle size={14} className="mr-1" /> Low Stock
          </TabsTrigger>
          <TabsTrigger value="expiry" data-testid="tab-expiry">
            <Clock size={14} className="mr-1" /> Expiry Stock
          </TabsTrigger>
          <TabsTrigger value="patients" data-testid="tab-patients">
            <Users size={14} className="mr-1" /> Patients
          </TabsTrigger>
        </TabsList>

        {/* SALES REPORT */}
        <TabsContent value="sales" className="space-y-4">
          {errors.sales && (
            <div className="flex items-center gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              <AlertCircle size={16} />
              {errors.sales}
            </div>
          )}
          {loading.sales && (
            <div className="flex items-center gap-2 rounded-md border border-border bg-white p-3 text-sm text-muted-foreground">
              <Loader2 size={16} className="animate-spin" />
              Loading sales report...
            </div>
          )}

          {/* Simplified Staff-Friendly Sales Statistics */}
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
            <Stat
              label={datePreset === "today" ? "Today's Sales" : "Total Sales"}
              value={inr(sales?.totals?.gross_sales || 0)}
              sub={`${sales?.totals?.bill_count || 0} bills`}
            />
            <Stat
              label={datePreset === "today" ? "Bills Today" : "Total Bills"}
              value={sales?.totals?.bill_count || 0}
              sub="Invoices printed"
            />
            <Stat
              label="Sales Returns"
              value={inr(sales?.totals?.sales_returns || 0)}
            />
            <Stat
              testid="sales-grand-total"
              label="Final Sales"
              value={inr((sales?.totals?.net_sales ?? sales?.totals?.grand_total) || 0)}
              tone="primary"
              sub="After returns"
            />
            <Stat
              label="GST Collected"
              value={inr(sales?.totals?.total_tax || 0)}
              sub="CGST + SGST"
            />
            <Stat
              label="Total Discount"
              value={inr(sales?.totals?.discount || 0)}
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="rounded-md border border-border bg-white p-5">
              <div className="text-sm font-semibold mb-3" style={{ fontFamily: "Chivo" }}>By Payment Mode</div>
              <div className="space-y-2">
                {sales &&
                  Object.entries(sales.by_payment_mode).map(([k, v]) => {
                    const total = sales.totals.grand_total || 1;
                    const pct = (v / total) * 100;
                    return (
                      <div key={k}>
                        <div className="flex justify-between text-sm">
                          <span>{k}</span>
                          <span className="tabular-nums">{inr(v)} · {pct.toFixed(1)}%</span>
                        </div>
                        <div className="h-1.5 bg-secondary rounded-full mt-1 overflow-hidden">
                          <div className="h-full bg-primary" style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>

            <div className="rounded-md border border-border bg-white p-5">
              <div className="text-sm font-semibold mb-3" style={{ fontFamily: "Chivo" }}>Top Selling Medicines</div>
              <div className="space-y-1 text-sm">
                {(sales?.top_items || []).map((it, i) => (
                  <div key={i} className="flex justify-between py-1 border-b border-border last:border-0">
                    <span className="truncate">{i + 1}. {it.name}</span>
                    <span className="tabular-nums text-muted-foreground">{it.quantity} · {inr(it.amount)}</span>
                  </div>
                ))}
                {(!sales?.top_items || sales.top_items.length === 0) && (
                  <div className="text-muted-foreground py-3 text-center">No sales in this range.</div>
                )}
              </div>
            </div>
          </div>

          <div className="rounded-md border border-border bg-white overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b border-border">
              <div className="text-sm font-semibold" style={{ fontFamily: "Chivo" }}>
                Sales Bills ({salesRows.length}) · {dateRangeLabel}
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => printReport("Sales", salesRows, salesColumns, salesSummary)} data-testid="print-sales-report">
                  <Printer size={14} className="mr-1" /> Print Sales Report
                </Button>
                <Button variant="outline" size="sm" onClick={() => downloadCSV(csvFileName("sales"), salesRows)} data-testid="export-sales-csv">
                  <Download size={14} className="mr-1" /> Export Sales CSV
                </Button>
              </div>
            </div>
            <div className="max-h-[420px] overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Bill No.</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Payment Mode</TableHead>
                    <TableHead className="text-right">Items</TableHead>
                    <TableHead className="text-right">Tax</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {salesRows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                        No bills in this range.
                      </TableCell>
                    </TableRow>
                  )}
                  {salesRows.map((r) => (
                    <TableRow
                      key={r.bill_no}
                      className="data-row cursor-pointer"
                      onClick={() => openBill(r.bill_no)}
                      data-testid={`sales-row-${r.bill_no}`}
                    >
                      <TableCell className="font-mono text-xs">
                        <div className="flex items-center gap-1">
                          <Printer size={12} className="text-muted-foreground" />
                          {r.bill_no}
                        </div>
                      </TableCell>
                      <TableCell className="text-xs whitespace-nowrap">{r.date}</TableCell>
                      <TableCell className="text-sm font-medium">{r.customer || "Walk-in Customer"}</TableCell>
                      <TableCell>
                        <Badge variant="secondary">{r.raw_payment_mode === "Split" ? "Split" : r.payment_mode}</Badge>
                        {r.raw_payment_mode === "Split" && Array.isArray(r.payments) && r.payments.length > 0 && (
                          <div className="text-[10px] text-muted-foreground mt-0.5 whitespace-nowrap">
                            {r.payments.map((p) => `${p.mode} ${inr(p.amount)}`).join(" + ")}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{r.items}</TableCell>
                      <TableCell className="text-right tabular-nums">{inr((r.cgst || 0) + (r.sgst || 0))}</TableCell>
                      <TableCell className="text-right tabular-nums font-semibold">{inr(r.grand_total)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        </TabsContent>

        {/* PURCHASES REPORT */}
        <TabsContent value="purchases" className="space-y-4">
          {errors.purchases && (
            <div className="flex items-center gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              <AlertCircle size={16} />
              {errors.purchases}
            </div>
          )}
          {loading.purchases && (
            <div className="flex items-center gap-2 rounded-md border border-border bg-white p-3 text-sm text-muted-foreground">
              <Loader2 size={16} className="animate-spin" />
              Loading purchases report...
            </div>
          )}
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
            <Stat label="Gross Purchases" value={inr(purchases?.totals?.gross_purchases || 0)} sub={`${purchases?.totals?.purchase_count || 0} invoices`} />
            <Stat label="Purchase Returns" value={inr(purchases?.totals?.purchase_returns || 0)} />
            <Stat label="Net Purchases" value={inr((purchases?.totals?.net_purchases ?? purchases?.totals?.total) || 0)} tone="primary" />
            <Stat label="Subtotal" value={inr(purchases?.totals?.subtotal || 0)} />
            <Stat label="Total Tax" value={inr(purchases?.totals?.tax || 0)} />
            <Stat label="Suppliers" value={purchases?.by_supplier?.length || 0} />
          </div>

          <div className="rounded-md border border-border bg-white overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b border-border">
              <div className="text-sm font-semibold" style={{ fontFamily: "Chivo" }}>
                Purchase Invoices ({purchaseRows.length}) · {dateRangeLabel}
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => downloadCSV(csvFileName("purchases"), purchaseRows)} data-testid="export-purchases-csv">
                  <Download size={14} className="mr-1" /> Export Purchases CSV
                </Button>
              </div>
            </div>
            <div className="max-h-[420px] overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Invoice No.</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Supplier</TableHead>
                    <TableHead className="text-right">Items</TableHead>
                    <TableHead className="text-right">Subtotal</TableHead>
                    <TableHead className="text-right">Tax</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {purchaseRows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                        No purchases in this range.
                      </TableCell>
                    </TableRow>
                  )}
                  {purchaseRows.map((r) => (
                    <TableRow key={r.invoice_no} className="data-row">
                      <TableCell className="font-mono text-xs">{r.invoice_no}</TableCell>
                      <TableCell className="text-xs whitespace-nowrap">{r.date}</TableCell>
                      <TableCell>{r.supplier}</TableCell>
                      <TableCell className="text-right tabular-nums">{r.items}</TableCell>
                      <TableCell className="text-right tabular-nums">{inr(r.subtotal)}</TableCell>
                      <TableCell className="text-right tabular-nums">{inr(r.tax)}</TableCell>
                      <TableCell className="text-right tabular-nums font-semibold">{inr(r.total)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        </TabsContent>

        {/* FULL STOCK REPORT */}
        <TabsContent value="stock" className="space-y-4">
          {errors.stock && (
            <div className="flex items-center gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              <AlertCircle size={16} />
              {errors.stock}
            </div>
          )}
          {loading.stock && (
            <div className="flex items-center gap-2 rounded-md border border-border bg-white p-3 text-sm text-muted-foreground">
              <Loader2 size={16} className="animate-spin" />
              Loading stock report...
            </div>
          )}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat testid="stock-value-cost" label="Stock @ Cost" value={inr(stock?.totals?.stock_value_cost || 0)} sub={`${stock?.totals?.total_units || 0} units`} tone="primary" />
            <Stat label="Stock @ MRP" value={inr(stock?.totals?.stock_value_mrp || 0)} sub={`Profit potential ${inr(stock?.totals?.potential_profit || 0)}`} />
            <Stat label="Low / Out" value={`${stock?.totals?.low_stock_count || 0} / ${stock?.totals?.out_of_stock_count || 0}`} sub="items" tone="accent" />
            <Stat label="Expiring / Expired" value={`${stock?.totals?.expiring_count || 0} / ${stock?.totals?.expired_count || 0}`} sub="in 60 days" />
          </div>

          <div className="rounded-md border border-border bg-white overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b border-border">
              <div className="text-sm font-semibold" style={{ fontFamily: "Chivo" }}>
                Full Inventory ({stockRows.length}) · As on {formatDate(todayISO())}
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => printReport("Stock", stockRows, stockColumns, stockSummary)} data-testid="print-stock-report">
                  <Printer size={14} className="mr-1" /> Print Stock Report
                </Button>
                <Button variant="outline" size="sm" onClick={() => downloadCSV(csvFileName("stock"), stockRows)} data-testid="export-stock-csv">
                  <Download size={14} className="mr-1" /> Export Stock CSV
                </Button>
              </div>
            </div>
            <div className="max-h-[500px] overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Batch / Expiry</TableHead>
                    <TableHead className="text-right">Stock</TableHead>
                    <TableHead className="text-right">MRP</TableHead>
                    <TableHead className="text-right">Cost</TableHead>
                    <TableHead className="text-right">Value @ Cost</TableHead>
                    <TableHead className="text-right">Value @ MRP</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {stockRows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                        No inventory yet.
                      </TableCell>
                    </TableRow>
                  )}
                  {stockRows.map((r, i) => (
                    <TableRow key={i} className="data-row">
                      <TableCell>
                        <div className="font-medium">{r.name}</div>
                        <div className="text-xs text-muted-foreground">{r.manufacturer} · HSN {r.hsn || "—"}</div>
                      </TableCell>
                      <TableCell className="text-xs">
                        <div className="font-mono">{r.batch || "—"}</div>
                        <div className="text-muted-foreground">{r.expiry || "—"}</div>
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {r.stock === 0 ? <Badge variant="destructive">Out</Badge> : r.stock}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{inr(r.mrp)}</TableCell>
                      <TableCell className="text-right tabular-nums">{inr(r.purchase_price)}</TableCell>
                      <TableCell className="text-right tabular-nums">{inr(r.value_at_cost)}</TableCell>
                      <TableCell className="text-right tabular-nums">{inr(r.value_at_mrp)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        </TabsContent>

        {/* BATCH-BASED EXPIRY STOCK REPORT */}
        <TabsContent value="expiry" className="space-y-4">
          {errors.expiry && (
            <div className="flex items-center gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              <AlertCircle size={16} />
              {errors.expiry}
            </div>
          )}
          {loading.expiry && (
            <div className="flex items-center gap-2 rounded-md border border-border bg-white p-3 text-sm text-muted-foreground">
              <Loader2 size={16} className="animate-spin" />
              Loading batch expiry report...
            </div>
          )}

          {/* Expiry Filters Bar */}
          <div className="flex flex-wrap items-center justify-between gap-3 bg-white p-3 border border-border rounded-md">
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-muted-foreground">Expiry Range:</span>
              {[
                { id: "expired", label: "Expired" },
                { id: "15", label: "Next 15 Days" },
                { id: "30", label: "Next 30 Days" },
                { id: "45", label: "Next 45 Days" },
                { id: "60", label: "Next 60 Days" },
                { id: "90", label: "Next 90 Days" },
                { id: "all_expiring", label: "All Expiring (90d)" },
                { id: "all", label: "All Batches" },
              ].map((f) => (
                <Button
                  key={f.id}
                  variant={expiryFilter === f.id ? "default" : "outline"}
                  size="sm"
                  onClick={() => handleExpiryFilterChange(f.id)}
                  data-testid={`expiry-filter-${f.id}`}
                  className="text-xs h-8"
                >
                  {f.label}
                </Button>
              ))}
            </div>

            <div className="text-xs text-muted-foreground">
              Batch-based FEFO · Available Stock &gt; 0
            </div>
          </div>

          {/* Expiry Summary Stats */}
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
            <Stat
              label="Expired Batches"
              value={expiryReport?.summary?.expired_count || 0}
              sub="Action required"
              tone={(expiryReport?.summary?.expired_count || 0) > 0 ? "accent" : "default"}
              testid="stat-expired-batches"
            />
            <Stat
              label="Next 30 Days"
              value={expiryReport?.summary?.expiring_30_count || 0}
              sub="High priority"
            />
            <Stat
              label="Next 60 Days"
              value={expiryReport?.summary?.expiring_60_count || 0}
              sub="Medium priority"
            />
            <Stat
              label="Next 90 Days"
              value={expiryReport?.summary?.expiring_90_count || 0}
              sub="Batches tracked"
            />
            <Stat
              label="Total Stock Units"
              value={expiryReport?.summary?.total_units || 0}
              sub="Units in batches"
            />
            <Stat
              label="Stock Value @ Cost"
              value={inr(expiryReport?.summary?.total_value_cost || 0)}
              sub="At risk value"
              tone="primary"
            />
          </div>

          {/* Expiry Table */}
          <div className="rounded-md border border-border bg-white overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b border-border">
              <div className="text-sm font-semibold" style={{ fontFamily: "Chivo" }}>
                Expiry Stock Batches ({expiryRows.length}) · As on {formatDate(todayISO())}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => printReport("Expiry Stock", expiryRows, expiryColumns, expirySummary)}
                  data-testid="print-expiry-report"
                >
                  <Printer size={14} className="mr-1" /> Print Expiry Report
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => downloadCSV(csvFileName("expiry_stock"), expiryRows)}
                  data-testid="export-expiry-csv"
                >
                  <Download size={14} className="mr-1" /> Export Expiry CSV
                </Button>
              </div>
            </div>
            <div className="max-h-[500px] overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Medicine Name</TableHead>
                    <TableHead>Batch No.</TableHead>
                    <TableHead>Expiry Date</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Available Stock</TableHead>
                    <TableHead className="text-right">Cost Price</TableHead>
                    <TableHead className="text-right">MRP</TableHead>
                    <TableHead className="text-right">Value @ Cost</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {expiryRows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                        No medicine batches match this expiry criteria.
                      </TableCell>
                    </TableRow>
                  )}
                  {expiryRows.map((r, i) => (
                    <TableRow key={i} className="data-row">
                      <TableCell>
                        <div className="font-medium text-foreground">{r.medicine}</div>
                        {r.generic && <div className="text-xs text-muted-foreground">{r.generic}</div>}
                      </TableCell>
                      <TableCell className="font-mono text-xs font-semibold">{r.batch_no}</TableCell>
                      <TableCell className="text-xs whitespace-nowrap font-medium">{r.expiry_date}</TableCell>
                      <TableCell>
                        {r.status === "Expired" ? (
                          <Badge variant="destructive" className="text-xs">
                            Expired ({Math.abs(r.days_until_expiry)}d ago)
                          </Badge>
                        ) : (
                          <Badge variant="secondary" className="bg-amber-100 text-amber-900 border-amber-200 text-xs">
                            Expiring in {r.days_until_expiry}d
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right tabular-nums font-semibold">
                        {r.stock} <span className="text-xs font-normal text-muted-foreground">{r.unit}</span>
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{inr(r.purchase_price)}</TableCell>
                      <TableCell className="text-right tabular-nums">{inr(r.mrp)}</TableCell>
                      <TableCell className="text-right tabular-nums font-semibold">{inr(r.value_at_cost)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        </TabsContent>

        {/* LOW STOCK REPORT */}
        <TabsContent value="low-stock" className="space-y-4">
          {errors.lowStock && (
            <div className="flex items-center gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              <AlertCircle size={16} />
              {errors.lowStock}
            </div>
          )}
          {loading.lowStock && (
            <div className="flex items-center gap-2 rounded-md border border-border bg-white p-3 text-sm text-muted-foreground">
              <Loader2 size={16} className="animate-spin" />
              Loading low stock report...
            </div>
          )}

          {/* Low Stock Filters Bar */}
          <div className="flex flex-wrap items-center justify-between gap-3 bg-white p-3 border border-border rounded-md">
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-muted-foreground">Stock Status:</span>
              {[
                { id: "all", label: "All Items to Reorder" },
                { id: "out_of_stock", label: "Out of Stock (0)" },
                { id: "low_stock", label: "Low Stock (≤ Threshold)" },
              ].map((f) => (
                <Button
                  key={f.id}
                  variant={lowStockFilter === f.id ? "default" : "outline"}
                  size="sm"
                  onClick={() => setLowStockFilter(f.id)}
                  data-testid={`low-stock-filter-${f.id}`}
                  className="text-xs h-8"
                >
                  {f.label}
                </Button>
              ))}
            </div>

            <div className="text-xs text-muted-foreground">
              Rule: Current Stock ≤ Low Stock Alert Threshold
            </div>
          </div>

          {/* Low Stock Summary Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat
              label="Out of Stock"
              value={lowStockReport?.summary?.out_of_stock_count || 0}
              sub="Zero units available"
              tone={(lowStockReport?.summary?.out_of_stock_count || 0) > 0 ? "accent" : "default"}
              testid="stat-out-of-stock"
            />
            <Stat
              label="Low Stock Items"
              value={lowStockReport?.summary?.low_stock_count || 0}
              sub="Below alert threshold"
            />
            <Stat
              label="Reorder Deficit"
              value={`${lowStockReport?.summary?.total_deficit_units || 0} units`}
              sub="Required to reach threshold"
            />
            <Stat
              label="Current Value @ Cost"
              value={inr(lowStockReport?.summary?.total_value_cost || 0)}
              sub="Existing stock value"
              tone="primary"
            />
          </div>

          {/* Low Stock Table */}
          <div className="rounded-md border border-border bg-white overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b border-border">
              <div className="text-sm font-semibold" style={{ fontFamily: "Chivo" }}>
                Low Stock &amp; Reorder List ({lowStockRows.length}) · As on {formatDate(todayISO())}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => printReport("Low Stock", lowStockRows, lowStockColumns, lowStockSummary)}
                  data-testid="print-low-stock-report"
                >
                  <Printer size={14} className="mr-1" /> Print Low Stock Report
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => downloadCSV(csvFileName("low_stock"), lowStockRows)}
                  data-testid="export-low-stock-csv"
                >
                  <Download size={14} className="mr-1" /> Export Low Stock CSV
                </Button>
              </div>
            </div>
            <div className="max-h-[500px] overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Medicine</TableHead>
                    <TableHead>Manufacturer / Category</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Current Stock</TableHead>
                    <TableHead className="text-right">Threshold</TableHead>
                    <TableHead className="text-right">Reorder Deficit</TableHead>
                    <TableHead className="text-right">Cost Price</TableHead>
                    <TableHead className="text-right">Value @ Cost</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {lowStockRows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                        All medicines are well-stocked above their thresholds.
                      </TableCell>
                    </TableRow>
                  )}
                  {lowStockRows.map((r, i) => (
                    <TableRow key={i} className="data-row">
                      <TableCell className="font-medium text-foreground">{r.medicine}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {r.manufacturer} {r.category ? `· ${r.category}` : ""}
                      </TableCell>
                      <TableCell>
                        {r.status === "Out of Stock" ? (
                          <Badge variant="destructive" className="text-xs">Out of Stock</Badge>
                        ) : (
                          <Badge variant="secondary" className="bg-amber-100 text-amber-900 border-amber-200 text-xs">
                            Low Stock
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className={`text-right tabular-nums font-bold ${r.stock === 0 ? "text-destructive" : "text-amber-700"}`}>
                        {r.stock} <span className="text-xs font-normal text-muted-foreground">{r.unit}</span>
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-muted-foreground">{r.threshold}</TableCell>
                      <TableCell className="text-right tabular-nums font-semibold text-primary">
                        +{r.reorder_qty}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{inr(r.purchase_price)}</TableCell>
                      <TableCell className="text-right tabular-nums font-medium">{inr(r.value_at_cost)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        </TabsContent>

        {/* PATIENTS SALES REPORT */}
        <TabsContent value="patients" className="space-y-4">
          {errors.patients && (
            <div className="flex items-center gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              <AlertCircle size={16} />
              {errors.patients}
            </div>
          )}
          {loading.patients && (
            <div className="flex items-center gap-2 rounded-md border border-border bg-white p-3 text-sm text-muted-foreground">
              <Loader2 size={16} className="animate-spin" />
              Loading patient sales report...
            </div>
          )}

          {/* Patient Sales Report Header Title */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <div>
              <h2 className="text-xl font-semibold text-slate-900" style={{ fontFamily: "Chivo" }}>
                Patient Sales Report · {dateRangeLabel}
              </h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                Consolidated billing, returns, and final sales by registered patient
              </p>
            </div>
          </div>

          {/* Simplified Patient Sales Statistics Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            <Stat
              label="Total Patients"
              value={patientReport?.summary?.total_patients ?? (patientReport?.patients?.length || 0)}
              sub="Active patients"
              testid="stat-total-patients"
            />
            <Stat
              label="Total Bills"
              value={patientReport?.summary?.total_bills ?? 0}
              sub={`${patientReport?.summary?.total_items ?? 0} items`}
              testid="stat-total-bills"
            />
            <Stat
              label="Total Sales"
              value={inr(patientReport?.summary?.total_sales ?? 0)}
              sub="Gross sales"
              testid="stat-total-sales"
            />
            <Stat
              label="Total Returns"
              value={inr(patientReport?.summary?.total_returns ?? 0)}
              sub="Refunded sales"
              testid="stat-total-returns"
            />
            <Stat
              label="Final Sales"
              value={inr(patientReport?.summary?.final_sales ?? 0)}
              sub="Total Sales – Returns"
              tone={(patientReport?.summary?.final_sales ?? 0) < 0 ? "destructive" : "default"}
              testid="stat-final-sales"
            />
          </div>

          {/* Search Bar & Actions */}
          <div className="rounded-md border border-border bg-white p-4 space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="relative flex-1 max-w-sm">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={patientSearch}
                  onChange={(e) => setPatientSearch(e.target.value)}
                  placeholder="Search by patient name or phone…"
                  className="pl-9 bg-white"
                  data-testid="patient-report-search"
                />
              </div>

              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1.5"
                  onClick={() => setShowConsolidatedThermal(true)}
                  disabled={loading.patients || !patientReport}
                  data-testid="print-consolidated-thermal-btn"
                >
                  <Printer size={14} />
                  <span>Print Report</span>
                  <span className="text-[10px] text-muted-foreground">({["58mm", "72mm", "80mm"].includes(shopInfo?.thermal_paper_width) ? shopInfo.thermal_paper_width : "72mm"})</span>
                </Button>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const rows = patientReport?.patients?.map((p) => ({
                      Patient: p.patient_name,
                      Phone: p.phone,
                      Bills: p.bill_count,
                      Items: p.item_count,
                      "Total Sales": p.total_sales,
                      "Total Returns": p.total_returns,
                      "Final Sales": p.final_sales,
                    }));
                    downloadCSV(
                      datePreset === "all"
                        ? "patient-sales-report-all-time.csv"
                        : `patient-sales-report-${start}-to-${end}.csv`,
                      rows
                    );
                  }}
                  disabled={!patientReport?.patients?.length}
                  data-testid="export-patient-report-btn"
                >
                  <Download size={14} className="mr-1" /> Export CSV
                </Button>
              </div>
            </div>

            {/* Consolidated Patient Table */}
            <div className="rounded-md border border-border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/30">
                    <TableHead>Patient</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead className="text-center">Bills</TableHead>
                    <TableHead className="text-center">Items</TableHead>
                    <TableHead className="text-right">Total Sales</TableHead>
                    <TableHead className="text-right">Total Returns</TableHead>
                    <TableHead className="text-right font-bold">Final Sales</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(!patientReport?.patients || patientReport.patients.length === 0) && (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-12 text-muted-foreground">
                        <Users className="mx-auto mb-2 opacity-40" size={24} />
                        No patient sales or returns found for this period.
                      </TableCell>
                    </TableRow>
                  )}
                  {patientReport?.patients?.map((p) => (
                    <TableRow
                      key={p.patient_id}
                      className="cursor-pointer hover:bg-muted/40 transition-colors"
                      onClick={() => {
                        setSelectedPatientId(p.patient_id);
                        setPatientModalOpen(true);
                      }}
                      data-testid={`patient-row-${p.patient_id}`}
                    >
                      <TableCell className="font-medium text-slate-900">
                        {p.patient_name}
                      </TableCell>
                      <TableCell className="font-mono text-xs text-muted-foreground">
                        {p.phone || "—"}
                      </TableCell>
                      <TableCell className="text-center text-xs tabular-nums">
                        {p.bill_count}
                      </TableCell>
                      <TableCell className="text-center text-xs tabular-nums text-muted-foreground">
                        {p.item_count}
                      </TableCell>
                      <TableCell className="text-right text-xs tabular-nums">
                        {inr(p.total_sales)}
                      </TableCell>
                      <TableCell className="text-right text-xs tabular-nums text-amber-600">
                        {p.total_returns > 0 ? `-${inr(p.total_returns)}` : "₹0.00"}
                      </TableCell>
                      <TableCell className={`text-right text-xs tabular-nums font-semibold ${p.final_sales < 0 ? "text-red-600" : ""}`}>
                        {inr(p.final_sales)}
                      </TableCell>
                      <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 text-xs text-primary hover:text-primary/80 gap-1 px-2.5"
                          onClick={() => {
                            setSelectedPatientId(p.patient_id);
                            setPatientModalOpen(true);
                          }}
                          data-testid={`view-patient-${p.patient_id}`}
                        >
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* PATIENT DETAIL SALES REPORT MODAL */}
      <PatientSalesReportDialog
        open={patientModalOpen}
        onOpenChange={setPatientModalOpen}
        patientId={selectedPatientId}
        shop={shopInfo}
      />

      {/* CONSOLIDATED THERMAL PRINT MODAL */}
      {showConsolidatedThermal && (
        <Dialog open={showConsolidatedThermal} onOpenChange={setShowConsolidatedThermal}>
          <DialogContent
            className="sm:max-w-md max-h-[90vh] flex flex-col p-0 overflow-hidden receipt-print-dialog"
            data-testid="consolidated-thermal-dialog"
          >
            <DialogHeader className="px-5 py-3.5 border-b border-border shrink-0 bg-white no-print">
              <DialogTitle className="flex items-center justify-between text-base">
                <span>Thermal Report · {["58mm", "72mm", "80mm"].includes(shopInfo?.thermal_paper_width) ? shopInfo.thermal_paper_width : "72mm"}</span>
                <span className="text-xs font-normal text-muted-foreground">Patients Summary</span>
              </DialogTitle>
            </DialogHeader>

            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
              <div className="bg-muted/40 rounded-lg p-3 border border-border/60 flex justify-center receipt-preview-shell">
                <ConsolidatedPatientsThermalPrint
                  summary={patientReport?.summary}
                  patients={patientReport?.patients || []}
                  periodLabel={dateRangeLabel}
                  shop={shopInfo}
                  preview
                />
              </div>
              <div className="text-[11px] text-muted-foreground text-center no-print">
                Formatted for {["58mm", "72mm", "80mm"].includes(shopInfo?.thermal_paper_width) ? shopInfo.thermal_paper_width : "72mm"} thermal roll printer.
              </div>
            </div>

            <div className="px-5 py-3 border-t border-border shrink-0 bg-white flex justify-end gap-2 no-print">
              <Button variant="outline" size="sm" onClick={() => setShowConsolidatedThermal(false)}>
                Close
              </Button>
              <Button
                size="sm"
                className="gap-1.5"
                onClick={() => window.print()}
                data-testid="confirm-consolidated-print-btn"
              >
                <Printer size={15} />
                <span>Print</span>
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      <InvoicePreviewDialog
        open={!!previewBill}
        onOpenChange={(o) => !o && setPreviewBill(null)}
        bill={previewBill}
        shop={shopInfo}
        onDeleteBill={canDeleteBills ? openDeleteModal : undefined}
        onPaymentUpdated={(updatedBill) => {
          setPreviewBill(updatedBill);
          loadSales();
        }}
      />

      <DeleteBillDialog
        open={deleteModalOpen}
        onOpenChange={setDeleteModalOpen}
        bill={billToDelete}
        onSuccess={handleBillDeleted}
      />
    </div>
  );
}
